
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import { useI18n } from 'vue-i18n/index';
  import { Actions } from '@/store/enums/StoreEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2';

  export default defineComponent({
    name: 'newsletter-subscribers-listing',
    components: {
      Datatable,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (key) => (te(key) ? t(key) : key);
      const email = ref<string>('');
      const loading = ref(false);
      const totalAllSubscribers = ref(0);

      const filteredItemsCount = computed(
        () => store.getters.newsletterSubscribersCount
      );
      const tableData = computed(() => store.getters.newsletterSubscribersList);
      // const totalActiveSubscribers = tableData.value.filter(s => s.is_active).length

      const tableHeader = ref([
        { name: translate('ID'), key: 'id', sortable: true },
        { name: translate('email'), key: 'amount', sortable: true },
        {
          name: translate('subscribedAt'),
          key: 'subscribed_at',
          sortable: true,
        },
        { name: translate('isActive'), key: 'is_active', sortable: true },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const fetchNewsletterSubs = async (preserveTotalCount = false) => {
        loading.value = true;
        await store.dispatch(Actions.GET_NEWSLETTER_SUBSCRIBERS);
        if (!preserveTotalCount) {
          totalAllSubscribers.value = store.getters.newsletterSubscribersCount;
        }
        loading.value = false;
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(
          Actions.UPDATE_NEWSLETTER_SUBSCRIBERS_CURRENT_PAGE,
          val
        );
        await store.dispatch(Actions.GET_NEWSLETTER_SUBSCRIBERS);
        loading.value = false;
      };

      const onSubmitFilter = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_NEWSLETTER_SUBSCRIBERS, {
          email: email.value || '',
        });
        loading.value = false;
      };

      const onResetFilter = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.GET_NEWSLETTER_SUBSCRIBERS);
        totalAllSubscribers.value = store.getters.newsletterSubscribersCount;
        loading.value = false;
      };

      const formatDate = (d) =>
        new Date(d)
          .toLocaleString('sv-SE', { timeZone: 'Asia/Kuwait', hour12: false })
          .slice(0, 16);

      const toggleStatusUser = async (banner) => {
        const deletedMessage = banner.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_NEWSLETTER_SUBSCRIBER, banner.id);
          await store.dispatch(Actions.GET_NEWSLETTER_SUBSCRIBERS);

          totalAllSubscribers.value = store.getters.newsletterSubscribersCount;
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = banner.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('newsletterSubscribers'), [
          translate('subscriptions'),
        ]);
        await fetchNewsletterSubs();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      return {
        tableData,
        tableHeader,
        loading,
        translate,
        can,
        filteredItemsCount,
        totalAllSubscribers,
        // totalActiveSubscribers,
        pageChanged,
        formatDate,
        toggleStatusUser,
        onSubmitFilter,
        email,
        onResetFilter,
      };
    },
  });
